/**
 * VideoFileAdapter — a first-class input source, next to the camera.
 *
 * Frames come from a <video> element that plays a local file (or any URL)
 * instead of getUserMedia. It implements the SAME CameraAdapter interface, so
 * the detection loop and the UI treat "camera" and "file" identically.
 *
 * Why this exists (see TASK_PACKET.md):
 *  - no format conversion is needed,
 *  - every run is repeatable,
 *  - the owner can later replay a saved clip on his own phone.
 */

import type { CameraAdapter, CameraInfo } from "./types";

export class VideoFileAdapter implements CameraAdapter {
  private video: HTMLVideoElement | null = null;
  private srcUrl: string | null = null;
  private objectUrl: string | null = null;

  /** The detection loop only needs the frames; there are no devices to list. */
  async list(): Promise<CameraInfo[]> {
    return [];
  }

  /**
   * Load a File/Blob (picked with <input type=file>). Returns the object URL
   * that can be passed to start() as the `deviceId`.
   */
  loadFile(file: File): string {
    this.revokeObjectUrl();
    this.objectUrl = URL.createObjectURL(file);
    this.srcUrl = this.objectUrl;
    return this.objectUrl;
  }

  /**
   * Load a URL directly (e.g. a test clip served over localhost).
   * Passing it as the deviceId keeps the CameraAdapter contract.
   */
  loadUrl(url: string): string {
    this.revokeObjectUrl();
    this.srcUrl = url;
    return url;
  }

  async start(deviceId?: string): Promise<{ stream: MediaStream; video: HTMLVideoElement }> {
    if (deviceId) this.srcUrl = deviceId;
    if (!this.srcUrl) throw new Error("Geen videobestand gekozen");

    this.stop();

    const video = document.createElement("video");
    video.playsInline = true;
    video.muted = true;
    video.loop = false;
    // Needed to read pixels into a canvas when the file is cross-origin.
    video.crossOrigin = "anonymous";
    video.src = this.srcUrl;
    // Kick off playback without awaiting (play() can hang on slow/big files);
    // waitForReady below resolves on metadata or after a bounded timeout.
    void video.play().catch(() => {
      /* autoplay may be blocked; the caller can retry after a gesture */
    });
    await this.waitForReady(video);

    this.video = video;
    // A file video has no getUserMedia stream; captureStream() mirrors the
    // element's output where available (used for optional clip recording).
    const cap = (video as unknown as { captureStream?: () => MediaStream }).captureStream;
    const stream: MediaStream = typeof cap === "function" ? cap.call(video) : new MediaStream();
    return { stream, video };
  }

  stop(): void {
    if (this.video) {
      this.video.pause();
      this.video.removeAttribute("src");
      this.video.load();
      this.video = null;
    }
    this.revokeObjectUrl();
  }

  isActive(): boolean {
    return !!this.video && !this.video.paused && !this.video.ended;
  }

  private waitForReady(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve) => {
      if (video.readyState >= 2 && video.videoWidth > 0) return resolve();
      const on = () => {
        video.removeEventListener("loadeddata", on);
        video.removeEventListener("error", onErr);
        resolve();
      };
      const onErr = () => {
        video.removeEventListener("loadeddata", on);
        resolve();
      };
      video.addEventListener("loadeddata", on);
      video.addEventListener("error", onErr);
      setTimeout(on, 5000);
    });
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
  }
}
